import React, { useState, useEffect, useRef } from 'react';
import { Plus, Search } from 'lucide-react';
import { tagsApi } from '../../services/crmApi';
import { TagChip } from './TagChip';

interface Tag {
  id: number;
  name: string;
  color: string;
  icon?: string;
  is_system: boolean;
}

interface TagSelectorProps {
  applicationId: number;
  onTagsChange?: (tags: Tag[]) => void;
}

export const TagSelector: React.FC<TagSelectorProps> = ({ applicationId, onTagsChange }) => {
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [appTags, setAppTags] = useState<Tag[]>([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTags();
  }, [applicationId]);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const loadTags = async () => {
    try {
      const [all, app] = await Promise.all([
        tagsApi.getAllTags(),
        tagsApi.getApplicationTags(applicationId),
      ]);
      setAllTags(all);
      setAppTags(app);
    } catch (err) {
      console.error('Failed to load tags:', err);
    }
  };

  const handleAdd = async (tagId: number) => {
    setLoading(true);
    try {
      const updated = await tagsApi.addTag(applicationId, tagId);
      setAppTags(updated);
      onTagsChange?.(updated);
    } catch (err) {
      console.error('Failed to add tag:', err);
    }
    setLoading(false);
  };

  const handleRemove = async (tagId: number) => {
    setLoading(true);
    try {
      const updated = await tagsApi.removeTag(applicationId, tagId);
      setAppTags(updated);
      onTagsChange?.(updated);
    } catch (err) {
      console.error('Failed to remove tag:', err);
    }
    setLoading(false);
  };

  const appTagIds = new Set(appTags.map(t => t.id));
  const available = allTags
    .filter(t => !appTagIds.has(t.id))
    .filter(t => !search || t.name.toLowerCase().includes(search.toLowerCase()));

  const isBlacklisted = appTags.some(t => t.name === 'Blacklist');

  return (
    <div ref={ref} className="space-y-2">
      {/* Blacklist warning */}
      {isBlacklisted && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-red-700 text-xs font-medium flex items-center gap-2">
          <span className="text-red-500">&#9888;</span>
          Klient na czarnej liscie (Blacklist)
        </div>
      )}

      {/* Current tags */}
      <div className="flex flex-wrap gap-1">
        {appTags.map(tag => (
          <TagChip key={tag.id} tag={tag} onRemove={!loading ? () => handleRemove(tag.id) : undefined} />
        ))}
        <button
          onClick={() => setOpen(!open)}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full border border-dashed border-gray-300 transition-colors"
        >
          <Plus size={12} /> Dodaj
        </button>
      </div>

      {/* Dropdown */}
      {open && (
        <div className="bg-white border rounded-lg shadow-lg p-2 space-y-2 z-50">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-2 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Szukaj tagu..."
              className="w-full pl-7 pr-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-300"
              autoFocus
            />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-0.5">
            {available.length === 0 && (
              <p className="text-xs text-gray-400 py-2 text-center">Brak dostepnych tagow</p>
            )}
            {available.map(tag => (
              <button
                key={tag.id}
                onClick={() => { handleAdd(tag.id); setSearch(''); }}
                disabled={loading}
                className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
              >
                <TagChip tag={tag} size="sm" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
